import { useMemo, useRef, useState } from "react";
import {
  buildDaySchedule,
  isSlotOpen,
  minToTimeLabel,
  type AvailabilityExceptionRow,
  type AvailabilityWindowRow,
  type BusyItem,
  type DaySegment,
} from "./availability.js";
import { minutesOfDayInZone, ymdOfInstant } from "./zone.js";

const PX_PER_MIN = 1.1;
const SNAP_MIN = 15;

export interface GridResource {
  id: string;
  name: string;
}

interface Props {
  date: string; // YYYY-MM-DD
  resources: GridResource[];
  windows: AvailabilityWindowRow[];
  exceptions: AvailabilityExceptionRow[];
  busy: BusyItem[];
  /** Duration of the service being booked, in minutes. Null disables click-to-select (pure
   *  browse mode) -- used when no service has been picked yet. */
  slotDurationMin: number | null;
  selected?: { resourceId: string; startMin: number } | null;
  onSelectSlot?: (resourceId: string, startMin: number) => void;
  /** Called when a booked block is clicked (only blocks that carry a bookingId). */
  onSelectBooking?: (bookingId: string) => void;
}

export function StaffAvailabilityGrid({ date, resources, windows, exceptions, busy, slotDurationMin, selected, onSelectSlot, onSelectBooking }: Props) {
  const { byResource, rangeStart, rangeEnd } = useMemo(
    () => buildDaySchedule(resources.map((r) => r.id), windows, exceptions, busy, date),
    [resources, windows, exceptions, busy, date]
  );

  const totalMin = rangeEnd - rangeStart;
  const hourMarks = useMemo(() => {
    const marks: number[] = [];
    const first = Math.ceil(rangeStart / 60) * 60;
    for (let m = first; m <= rangeEnd; m += 60) marks.push(m);
    return marks;
  }, [rangeStart, rangeEnd]);

  // "Today" and "now" are the business's (lib/zone.ts), not the browser's.
  const now = new Date();
  const isToday = ymdOfInstant(now) === date;
  const nowMin = minutesOfDayInZone(now);

  if (resources.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "var(--rc-text-faint)", fontSize: 13.5 }}>
        No staff/resources configured for this service yet.
      </div>
    );
  }

  return (
    <div className="rc-scrollbar" style={{ overflowX: "auto", border: "1px solid var(--rc-border)", borderRadius: "var(--rc-radius-md)" }}>
      <div style={{ display: "flex", minWidth: resources.length * 168 + 56 }}>
        {/* Time axis */}
        {/* Sticky: the hours stay in view while the staff columns scroll sideways. */}
        <div style={{ width: 56, flexShrink: 0, position: "sticky", left: 0, zIndex: 6, background: "var(--rc-surface-sunken)", borderRight: "1px solid var(--rc-border)" }}>
          <div style={{ height: 36, borderBottom: "1px solid var(--rc-border)" }} />
          <div style={{ position: "relative", height: totalMin * PX_PER_MIN }}>
            {hourMarks.map((m) => (
              <div
                key={m}
                style={{
                  position: "absolute",
                  top: (m - rangeStart) * PX_PER_MIN - 6,
                  right: 8,
                  fontSize: 11,
                  color: "var(--rc-text-faint)",
                }}
              >
                {minToTimeLabel(m)}
              </div>
            ))}
          </div>
        </div>

        {/* Resource columns */}
        {resources.map((resource) => (
          <ResourceColumn
            key={resource.id}
            resource={resource}
            segments={byResource.get(resource.id) ?? []}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            hourMarks={hourMarks}
            slotDurationMin={slotDurationMin}
            selected={selected?.resourceId === resource.id ? selected : null}
            onSelectSlot={onSelectSlot}
            onSelectBooking={onSelectBooking}
            nowMin={isToday ? nowMin : null}
          />
        ))}
      </div>
    </div>
  );
}

function ResourceColumn({
  resource,
  segments,
  rangeStart,
  rangeEnd,
  hourMarks,
  slotDurationMin,
  selected,
  onSelectSlot,
  onSelectBooking,
  nowMin,
}: {
  resource: GridResource;
  segments: DaySegment[];
  rangeStart: number;
  rangeEnd: number;
  hourMarks: number[];
  slotDurationMin: number | null;
  selected: { resourceId: string; startMin: number } | null;
  onSelectSlot?: (resourceId: string, startMin: number) => void;
  onSelectBooking?: (bookingId: string) => void;
  nowMin: number | null;
}) {
  const colRef = useRef<HTMLDivElement>(null);
  const [hoverMin, setHoverMin] = useState<number | null>(null);
  const totalMin = rangeEnd - rangeStart;

  const openFill = segments.filter((s) => s.kind === "open");
  const closedFill: DaySegment[] = invertToClosed(segments, rangeStart, rangeEnd);

  function minuteFromClientY(clientY: number): number {
    const rect = colRef.current!.getBoundingClientRect();
    const y = clientY - rect.top;
    const rawMin = rangeStart + y / PX_PER_MIN;
    return Math.round(rawMin / SNAP_MIN) * SNAP_MIN;
  }

  function handleMove(e: React.MouseEvent) {
    if (!slotDurationMin) return;
    setHoverMin(minuteFromClientY(e.clientY));
  }

  function handleClick(e: React.MouseEvent) {
    if (!slotDurationMin || !onSelectSlot) return;
    const m = minuteFromClientY(e.clientY);
    if (isSlotOpen(segments, m, slotDurationMin)) onSelectSlot(resource.id, m);
  }

  const hoverValid = hoverMin != null && slotDurationMin != null && isSlotOpen(segments, hoverMin, slotDurationMin);

  return (
    <div style={{ width: 168, flexShrink: 0, borderRight: "1px solid var(--rc-border)" }}>
      <div
        style={{
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12.5,
          fontWeight: 700,
          color: "var(--rc-text)",
          borderBottom: "1px solid var(--rc-border)",
          background: "var(--rc-surface-sunken)",
        }}
      >
        {resource.name}
      </div>
      <div
        ref={colRef}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverMin(null)}
        onClick={handleClick}
        style={{
          position: "relative",
          height: totalMin * PX_PER_MIN,
          cursor: slotDurationMin ? "pointer" : "default",
          background: "var(--rc-surface)",
        }}
      >
        {hourMarks.map((m) => (
          <div
            key={m}
            style={{
              position: "absolute",
              top: (m - rangeStart) * PX_PER_MIN,
              left: 0,
              right: 0,
              borderTop: "1px solid var(--rc-border)",
            }}
          />
        ))}

        {closedFill.map((s, i) => (
          <div
            key={`closed-${i}`}
            style={{
              position: "absolute",
              top: (s.startMin - rangeStart) * PX_PER_MIN,
              left: 0,
              right: 0,
              height: (s.endMin - s.startMin) * PX_PER_MIN,
              background:
                "repeating-linear-gradient(135deg, var(--rc-neutral-soft), var(--rc-neutral-soft) 6px, var(--rc-surface-sunken) 6px, var(--rc-surface-sunken) 12px)",
            }}
          />
        ))}

        {segments
          .filter((s) => s.kind === "busy" || s.kind === "hold")
          .map((s, i) => (
            <div
              key={`busy-${i}`}
              title={s.kind === "busy" && s.bookingId && onSelectBooking ? `${s.label} (click for details)` : s.label}
              onClick={
                s.kind === "busy" && s.bookingId && onSelectBooking
                  ? (e) => {
                      e.stopPropagation(); // a click on a booked block opens it; it never picks a new time underneath
                      onSelectBooking(s.bookingId!);
                    }
                  : undefined
              }
              style={{
                cursor: s.kind === "busy" && s.bookingId && onSelectBooking ? "pointer" : undefined,
                position: "absolute",
                top: (s.startMin - rangeStart) * PX_PER_MIN,
                left: 2,
                right: 2,
                height: Math.max(2, (s.endMin - s.startMin) * PX_PER_MIN - 2),
                borderRadius: 4,
                fontSize: 10.5,
                fontWeight: 600,
                padding: "2px 5px",
                overflow: "hidden",
                color: s.kind === "hold" ? "var(--rc-warning)" : "var(--rc-on-primary)",
                background: s.kind === "hold" ? "var(--rc-warning-soft)" : "var(--rc-primary)",
                border: s.kind === "hold" ? "1px dashed var(--rc-warning)" : "none",
              }}
            >
              {s.label}
            </div>
          ))}

        {hoverMin != null && slotDurationMin != null && (
          <div
            style={{
              position: "absolute",
              top: (hoverMin - rangeStart) * PX_PER_MIN,
              left: 2,
              right: 2,
              height: slotDurationMin * PX_PER_MIN,
              borderRadius: 4,
              background: hoverValid ? "var(--rc-success-soft)" : "var(--rc-danger-soft)",
              border: `1.5px solid ${hoverValid ? "var(--rc-success)" : "var(--rc-danger)"}`,
              pointerEvents: "none",
              boxSizing: "border-box",
              padding: "2px 6px",
              fontSize: 11,
              fontWeight: 700,
              color: hoverValid ? "var(--rc-success)" : "var(--rc-danger)",
              overflow: "hidden",
            }}
          >
            {minToTimeLabel(hoverMin)}
          </div>
        )}

        {selected && (
          <div
            style={{
              position: "absolute",
              top: (selected.startMin - rangeStart) * PX_PER_MIN,
              left: 2,
              right: 2,
              height: (slotDurationMin ?? SNAP_MIN) * PX_PER_MIN,
              borderRadius: 4,
              background: "var(--rc-primary-soft)",
              border: "2px solid var(--rc-primary)",
              pointerEvents: "none",
              boxSizing: "border-box",
              padding: "2px 6px",
              fontSize: 11,
              fontWeight: 700,
              color: "var(--rc-primary-soft-text)",
              overflow: "hidden",
            }}
          >
            {minToTimeLabel(selected.startMin)}
          </div>
        )}

        {nowMin != null && nowMin >= rangeStart && nowMin <= rangeEnd && (
          <div
            style={{
              position: "absolute",
              top: (nowMin - rangeStart) * PX_PER_MIN,
              left: 0,
              right: 0,
              height: 2,
              background: "var(--rc-danger)",
              pointerEvents: "none",
            }}
          />
        )}
        {openFill.length === 0 && <div />}
      </div>
    </div>
  );
}

function invertToClosed(segments: DaySegment[], rangeStart: number, rangeEnd: number): DaySegment[] {
  const covered = [...segments].sort((a, b) => a.startMin - b.startMin);
  const closed: DaySegment[] = [];
  let cursor = rangeStart;
  for (const s of covered) {
    if (s.startMin > cursor) closed.push({ startMin: cursor, endMin: s.startMin, kind: "closed" });
    cursor = Math.max(cursor, s.endMin);
  }
  if (cursor < rangeEnd) closed.push({ startMin: cursor, endMin: rangeEnd, kind: "closed" });
  return closed;
}
